import React, { useState } from "react";
import StatusBadge from "../StatusBadge";

const REQUIRED_STEPS = ["business_details", "services", "business_config", "activate"];

const STEP_LABELS = {
  business_details: "Company Profile",
  services: "Services",
  business_config: "Setup & Customization",
  knowledge_base: "Knowledge Base",
  test: "Test",
  activate: "Activate",
};

const SERVICE_NAMES = {
  customer_support: "Chat Support",
  document_rag: "Smart Document Search",
  voice_support: "Voice Support",
  // appointment_booking: "Appointment Booking",
};

const ReviewRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-t border-slate-100 py-3 first:border-t-0 first:pt-0">
    <dt className="shrink-0 text-sm text-slate-500">{label}</dt>
    <dd className="max-w-[65%] text-right text-sm font-medium text-slate-900 break-words">{value || "—"}</dd>
  </div>
);

const CheckRow = ({ label, status, required }) => {
  const isDone = status === "completed";
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
      <div className="flex items-center gap-2">
        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${isDone ? "bg-emerald-500" : "bg-slate-200"}`}>
          {isDone ? (
            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          ) : (
            <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          )}
        </span>
        <span className="text-sm font-medium text-slate-800">{label}</span>
        {required ? (
          <span className="text-[10px] font-bold uppercase tracking-wide text-rose-500">Required</span>
        ) : null}
      </div>
      <StatusBadge status={status || "pending"} />
    </div>
  );
};

const OnboardingStepActivate = ({
  tenant,
  services,
  configs,
  steps,
  onboarding,
  onActivate,
  isDisabled,
}) => {
  const [activating, setActivating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const resolvedSteps = [
    "business_details",
    "services",
    "business_config",
    "knowledge_base",
    "test",
  ].map((key) => {
    const live = (steps || []).find((s) => s.step_key === key);
    return { key, label: STEP_LABELS[key], status: live?.status || "pending", required: REQUIRED_STEPS.includes(key) };
  });

  const requiredIncomplete = resolvedSteps.filter((s) => s.required && s.status !== "completed");
  const canActivate = !isDisabled && requiredIncomplete.length === 0 && !activating && !success;

  const enabledServices = (services || []).filter((s) => s.status === "enabled");
  const configCount = (configs || []).length;

  // KB doc count is not available here without another fetch — show a summary label instead.
  // TODO (backend): Pass kbDocCount prop once GET /tenants/:tenantId/kb/documents is wired.

  const handleActivate = async () => {
    if (!canActivate) return;
    setActivating(true);
    setError("");
    setSuccess(false);
    try {
      await onActivate();
      setSuccess(true);
    } catch (err) {
      setError(err?.message || "Activation failed. Please try again.");
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Review the final state before activating this tenant. All required steps must be complete.
      </p>

      {/* Business details review */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h4 className="mb-3 text-sm font-semibold text-slate-950">Company Profile</h4>
        <dl>
          <ReviewRow label="Business name" value={tenant?.name} />
          <ReviewRow label="Industry" value={tenant?.industry} />
          <ReviewRow label="Plan" value={tenant?.plan} />
          <ReviewRow label="Owner" value={tenant?.owner_name} />
          <ReviewRow label="Owner email" value={tenant?.owner_email} />
        </dl>
      </section>

      {/* Selected services */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h4 className="mb-3 text-sm font-semibold text-slate-950">Selected Services</h4>
        {enabledServices.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No services enabled yet.</p>
        ) : (
          <ul className="space-y-2">
            {enabledServices.map((s) => (
              <li key={s.service_key} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
                <span className="text-sm font-medium text-slate-800">
                  {SERVICE_NAMES[s.service_key] || s.service_key}
                </span>
                <StatusBadge status={s.status} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Business configuration */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h4 className="mb-3 text-sm font-semibold text-slate-950">Setup & Customization</h4>
        {configCount === 0 ? (
          <p className="text-sm text-slate-400 italic">No configuration blocks defined.</p>
        ) : (
          <p className="text-sm text-slate-700">
            <span className="font-semibold">{configCount}</span> configuration block{configCount !== 1 ? "s" : ""} saved.
          </p>
        )}
      </section>

      {/* Knowledge base summary */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h4 className="mb-3 text-sm font-semibold text-slate-950">Knowledge Base</h4>
        {/* TODO (backend): Replace static text with live document count once GET /tenants/:tenantId/kb/documents is wired */}
        <p className="text-sm text-slate-500 italic">
          Knowledge base document count not available — wire to{" "}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">GET /tenants/:tenantId/kb/documents</code>{" "}
          to show count here.
        </p>
      </section>

      {/* Readiness checklist */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h4 className="mb-3 text-sm font-semibold text-slate-950">Readiness Status</h4>
        <div className="space-y-2">
          {resolvedSteps.map((step) => (
            <CheckRow key={step.key} label={step.label} status={step.status} required={step.required} />
          ))}
        </div>

        {requiredIncomplete.length > 0 ? (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <p className="text-xs font-semibold text-rose-800">
              {requiredIncomplete.length} required step{requiredIncomplete.length > 1 ? "s" : ""} incomplete
            </p>
            <ul className="mt-1 list-disc pl-4">
              {requiredIncomplete.map((s) => (
                <li key={s.key} className="text-xs text-rose-700">{s.label}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-medium text-emerald-800">
            ✓ All required steps are complete. Ready to activate.
          </div>
        )}
      </section>

      {/* Feedback */}
      {success ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
          <p className="text-sm font-semibold text-emerald-800">Tenant activated successfully.</p>
          <p className="mt-1 text-xs text-emerald-700">
            Services are now live. The tenant status has been updated to active.
          </p>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {/* Activate button */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={handleActivate}
          disabled={!canActivate}
          className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {activating ? (
            <>
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Activating…
            </>
          ) : success ? (
            "Activated ✓"
          ) : (
            "Activate Tenant"
          )}
        </button>

        {!canActivate && !success && !isDisabled ? (
          <p className="text-xs text-slate-500">
            Complete all required steps to enable activation.
          </p>
        ) : null}

        {isDisabled ? (
          <p className="text-xs text-slate-400">Read-only access — activation requires write permission.</p>
        ) : null}
      </div>
    </div>
  );
};

export default OnboardingStepActivate;
